import { PermissionsService } from "src/app/core/services/permissions.service";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import Swal from "sweetalert2";
import { Subscription } from "rxjs";
import { EventService } from "src/app/core/services/event.service";
import { TranslateService } from "@ngx-translate/core";
import { PropertiesService } from "../../services/properties.services";
import { environment } from "src/environments/environment";
import { PropertyValidationRulesService } from "src/app/core/services/propertyValidationRules.service";
import { AiIntegrationsService } from "src/app/core/services/ai-integrations.service";
import { AiWalletStateService } from "src/app/core/services/ai-wallet-state.service";
import {
  PropertyDocumentChecklistItem,
  PropertyDocumentType,
  PropertyDocumentsService,
} from "src/app/core/services/property-documents.service";
import { DOCUMENT_UPLOAD_MAX_BYTES } from "./document-upload.util";

@Component({
  selector: "app-documents",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.scss"],
})
export class DocumentsComponent implements OnInit, OnChanges {
  countDoc: number = 0;

  public environment = environment;

  @Input() propertyDocumentsData!: any;
  @Input() property: any;
  /** Formulário do separador Detalhes: tipo/estado/negócio atuais, mesmo por gravar. */
  @Input() detailsForm: any;

  // Documentos obrigatórios por tipo de imóvel (configuração da entidade).
  documentTypes: PropertyDocumentType[] = [];
  applicableDocuments: PropertyDocumentChecklistItem[] = [];
  /** Entidade com a regra ligada e imóvel criado depois de a ligar. */
  typedMode = false;
  /** Linhas da checklist, cruzadas com os documentos locais (gravados ou não). */
  checklistRows: Array<
    PropertyDocumentChecklistItem & { localFiles: string[]; localDocs: any[] }
  > = [];
  requiredMissingNames: string[] = [];
  /** Tipo pré-escolhido quando se anexa a partir de uma linha da checklist. */
  private pendingDocumentTypeId: number | null = null;
  private lastApplicableSignature = "";
  private applicableSub?: Subscription;
  private warningEvSub?: Subscription;

  loginUser!: {
    id?: string;
    role?: { id?: string; name?: string };
    stores?: any[];
    associatedUsers?: any;
  };

  documentDownloads: any[] = [];
  listDocuments: any[] = [];

  evSub?: Subscription;
  subscriptionRules?: Subscription;

  minDocuments = 0;

  documentsMinError: any | null = null;
  showDocumentsMinWarning = false;
  /** Secção com arrasto activo, para a dropzone dessa secção se acender. */
  draggingVisibility: string | null = null;
  canAddDocuments = false;
  selectedDocumentVisibility = "PRIVATE";
  documentsByVisibility: Record<string, any[]> = {
    COMMERCIAL: [],
    INTERNAL: [],
    PRIVATE: [],
  };
  documentSections = [
    {
      visibility: "COMMERCIAL",
      label: "t-m-properties.t-commercial-documents",
      tooltip: "t-m-properties.t-document-visibility-commercial-tooltip",
    },
    {
      visibility: "INTERNAL",
      label: "t-m-properties.t-internal-documents",
      tooltip: "t-m-properties.t-document-visibility-internal-tooltip",
    },
    {
      visibility: "PRIVATE",
      label: "t-m-properties.t-private-documents",
      tooltip: "t-m-properties.t-document-visibility-private-tooltip",
    },
  ];
  moveDocumentSectionsByVisibility: Record<string, any[]> = {
    COMMERCIAL: this.documentSections.filter(
      (section) => section.visibility !== "COMMERCIAL",
    ),
    INTERNAL: this.documentSections.filter(
      (section) => section.visibility !== "INTERNAL",
    ),
    PRIVATE: this.documentSections.filter(
      (section) => section.visibility !== "PRIVATE",
    ),
  };
  // AI Document Extraction
  /** Extração de documentos ativa para este utilizador (decidido no formulário). */
  @Input() aiExtractionAvailable = false;
  /** Pede a extração dos documentos já guardados (ids de PropertyFile). */
  @Output() aiExtractRequested = new EventEmitter<number[]>();
  @Output() aiDocExtracted = new EventEmitter<Record<string, any>>();
  aiDocLoading = false;
  aiDocSuccess = '';
  aiDocError = '';
  aiServicesLoaded = false;
  canViewAiIntegrations = false;
  activatedAiServices = new Set<string>();
  private readonly DOC_EXTRACTION_CODES = [
    "DOC_INTELLIGENCE_PROPERTY",
    "DOC_EXTRACTION",
    "DOCUMENT_EXTRACTION",
  ];

  constructor(
    private eventService: EventService,
    public translate: TranslateService,
    private propertiesService: PropertiesService,
    public permissionsService: PermissionsService,
    private propertyValidationService: PropertyValidationRulesService,
    private aiService: AiIntegrationsService,
    private aiWalletState: AiWalletStateService,
    private propertyDocumentsService: PropertyDocumentsService,
  ) {}

  extractFromDocument() {
    if (!this.canUseDocAiService()) {
      this.aiDocError = this.translate.instant("ai-feedback.serviceInactiveBody", {
        context: this.translate.instant("ai-feedback.context.docExtraction"),
      });
      return;
    }

    this.aiDocLoading = true;
    this.aiDocError = '';
    const idempotencyKey = `doc_extract_${this.property?.id ?? 'new'}_${Date.now()}`;
    const unsavedDocs: File[] = (this.propertyDocumentsData?.dataUpload ?? []).filter(
      (file: File) => !!file,
    );

    const request$ = unsavedDocs.length
      ? this.aiService.executeDocIntelligenceUpload(
          idempotencyKey,
          unsavedDocs,
          this.property?.id,
          JSON.stringify({
            attachments: unsavedDocs.map((doc: File) => ({
              fileName: doc.name,
              mimeType: doc.type,
            })),
          }),
        )
      : this.aiService.executeAiService(
          'DOC_INTELLIGENCE_PROPERTY',
          idempotencyKey,
          this.property?.id,
        );

    request$.subscribe({
      next: (res: any) => {
        this.applyExtractionResponse(res);
      },
      error: (err: any) => {
        if (this.isGatewayTimeoutError(err)) {
          this.aiService
            .waitForUsageResultByIdempotency(idempotencyKey, {
              maxAttempts: 24,
              intervalMs: 2500,
            })
            .subscribe({
              next: (res: any) => this.applyExtractionResponse(res),
              error: (pollErr: any) => {
                this.aiDocLoading = false;
                this.aiDocError = pollErr?.error?.message ?? pollErr?.message ?? "Erro";
              },
            });
          return;
        }
        this.aiDocLoading = false;
        this.aiDocError = err?.error?.message ?? err?.message ?? "Erro";
      },
    });

  }

  private isGatewayTimeoutError(err: any): boolean {
    const status = Number(err?.status ?? err?.error?.status ?? 0);
    if (status === 504) return true;
    const text = String(
      err?.error?.message ??
      err?.message ??
      '',
    ).toLowerCase();
    return text.includes('gateway timeout') || text.includes('504');
  }

  private applyExtractionResponse(res: any) {
    this.aiDocLoading = false;
    if (res?.ok && res?.output) {
      const normalizedOutput = this.normalizeDocExtractionOutput(res.output);
      this.aiDocSuccess = `Dados extraídos com sucesso (${res.creditsConsumed} crédito(s))`;
      this.aiDocExtracted.emit(normalizedOutput);
      this.aiWalletState.applyConsumption(
        res.remainingBalance,
        res.creditsConsumed ?? 0,
      );
      setTimeout(() => this.aiDocSuccess = "", 5000);
      return;
    }

    this.aiDocError = res?.errorMessage ?? "Erro na extração";
  }

  ngOnInit(): void {
    const { id, role, stores, associatedUsers } = this.propertiesService.getUserAuth();
    this.loginUser = { id, role, stores, associatedUsers };
    this.canViewAiIntegrations =
      this.permissionsService.hasPermission("VIEW_AI_INTEGRATIONS");

    this.evSub = this.eventService
      .on("documents-tab-active")
      .subscribe((p: any) => {
        const propertyId = p?.propertyId;
        if (propertyId) this.loadDownloads(propertyId);
        this.maybeReloadApplicableDocuments();
      });

    // O separador Detalhes avisa a cada alteração; só vamos ao servidor se
    // tipo/estado/negócio mudaram de facto (ver assinatura).
    this.warningEvSub = this.eventService
      .on("check-warning-add-property")
      .subscribe(() => this.maybeReloadApplicableDocuments());

    this.propertyDocumentsService.getActiveTypes().subscribe({
      next: (types) => {
        this.documentTypes = types ?? [];
        this.maybeReloadApplicableDocuments(true);
      },
      error: () => (this.documentTypes = []),
    });

    this.subscriptionRules = this.propertyValidationService
      .rules$()
      .subscribe((map) => {
        if (!map?.size) return;

        this.refreshDocumentsWarnings();
      });

    this.loadActivatedAiServices();
    this.normalizeDocumentsVisibility();
    this.refreshDocumentPermissions();

      
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyDocumentsData'] && this.propertyDocumentsData) {
      this.normalizeDocumentsVisibility();
      // O pai recria este objecto ao (re)carregar o imóvel: repõe-se o aviso
      // dos documentos tipados, que só este componente sabe calcular.
      if (this.applicableDocuments.length) this.refreshDocumentsWarnings();
    }

    if (changes['property']) {
      this.refreshDocumentPermissions();
    }

    if (changes['property'] || changes['detailsForm']) {
      this.maybeReloadApplicableDocuments();
    }
  }

  private normalizeDocumentsVisibility() {
    if (!this.propertyDocumentsData) return;
    this.propertyDocumentsData.data = (this.propertyDocumentsData?.data ?? []).map((doc: any) =>
      this.normalizeDocumentVisibility(doc),
    );
    this.refreshDocumentsByVisibility();
  }

  private normalizeDocumentVisibility(doc: any) {
    const documentVisibility =
      doc?.documentVisibility ??
      (doc?.description === "finishingMapPropertyDocument" ? "COMMERCIAL" : "PRIVATE");
    return {
      ...doc,
      documentVisibility,
      showOnWebsite:
        doc?.showOnWebsite ?? (doc?.description === "finishingMapPropertyDocument"),
    };
  }

  private refreshDocumentsByVisibility() {
    this.documentsByVisibility = {
      COMMERCIAL: [],
      INTERNAL: [],
      PRIVATE: [],
    };

    (this.propertyDocumentsData?.data ?? []).forEach((doc: any) => {
      const visibility = doc?.documentVisibility ?? "PRIVATE";
      const key = this.documentsByVisibility[visibility] ? visibility : "PRIVATE";
      this.documentsByVisibility[key].push(doc);
    });

    this.refreshChecklist();
  }

  // ------------------------------------------- documentos obrigatórios (tipados)

  /** Há tipos de documento na entidade: mostra-se a coluna "Tipo de documento". */
  get hasDocumentTypes(): boolean {
    return this.documentTypes.length > 0;
  }

  private applicableSignature(): string {
    const d = this.detailsForm?.data ?? {};
    return [
      this.property?.id ?? "",
      d.propertyType ?? "",
      d.condition ?? "",
      d.businessType ?? "",
    ].join("|");
  }

  /**
   * Pede ao servidor o que se aplica a este imóvel (tipo/estado/negócio do
   * formulário, mesmo por gravar). Sem tipo escolhido ainda não há o que pedir.
   */
  private maybeReloadApplicableDocuments(force = false) {
    if (!this.hasDocumentTypes) return;
    const d = this.detailsForm?.data ?? {};
    const propertyTypeId = d.propertyType ?? this.property?.propertyType?.id ?? null;
    if (!propertyTypeId && !this.property?.id) return;

    const signature = this.applicableSignature();
    if (!force && signature === this.lastApplicableSignature) return;
    this.lastApplicableSignature = signature;

    this.applicableSub?.unsubscribe();
    this.applicableSub = this.propertyDocumentsService
      .getApplicable({
        propertyId: this.property?.id ?? null,
        propertyTypeId,
        propertyConditionId: d.condition ?? null,
        propertyBusinessTypeId: d.businessType ?? null,
      })
      .subscribe({
        next: (checklist) => {
          this.typedMode = !!checklist?.typedMode;
          this.applicableDocuments = checklist?.items ?? [];
          this.refreshChecklist();
          this.refreshDocumentsWarnings();
        },
        error: () => {
          this.typedMode = false;
          this.applicableDocuments = [];
          this.refreshChecklist();
        },
      });
  }

  /**
   * Cruza os documentos aplicáveis com os documentos locais: os já gravados e
   * os acabados de anexar (ainda sem id) contam da mesma forma.
   */
  private refreshChecklist() {
    const docs: any[] = this.propertyDocumentsData?.data ?? [];
    this.checklistRows = (this.applicableDocuments ?? []).map((item) => {
      // Os documentos que cumprem a linha ficam calculados AQUI, uma vez, e
      // não num método chamado pelo template: um `*ngFor` sobre um array novo
      // a cada ciclo dispara "expression changed after it was checked" em
      // cadeia — foi o "loop infinito" ao voltar ao separador.
      const localDocs = docs.filter(
        (doc) => Number(doc?.propertyDocumentTypeId) === item.documentTypeId,
      );
      const localFiles = localDocs
        .map((doc) => doc?.name)
        .filter((name) => !!name);
      return { ...item, done: localFiles.length > 0, localFiles, localDocs };
    });
    this.requiredMissingNames = this.checklistRows
      .filter((row) => row.isRequired && !row.done)
      .map((row) => row.name);
    if (this.propertyDocumentsData) {
      // O pai lê isto para o resumo de avisos e para o modal de ativação.
      this.propertyDocumentsData.typedMode = this.typedMode;
      this.propertyDocumentsData.requiredMissingNames = this.requiredMissingNames;
    }
  }

  trackByChecklistRow(_: number, row: { documentTypeId: number }) {
    return row.documentTypeId;
  }

  documentTypeName(id: number | null | undefined): string {
    if (!id) return "";
    return this.documentTypes.find((t) => t.id === Number(id))?.name ?? "";
  }

  /** "Anexar" numa linha da checklist: abre o input já com o tipo escolhido. */
  attachForType(row: PropertyDocumentChecklistItem) {
    this.addDoc(row.defaultVisibility ?? "PRIVATE", row.documentTypeId);
  }

  onDocumentTypeChanged(doc: any) {
    if (doc && (doc.propertyDocumentTypeId === undefined || doc.propertyDocumentTypeId === "")) {
      doc.propertyDocumentTypeId = null;
    }
    this.refreshChecklist();
    this.refreshDocumentsWarnings();
  }

  trackByDocumentSection(_: number, section: any) {
    return section?.visibility;
  }

  trackByDocument(_: number, doc: any) {
    return doc?.id ?? doc?.order ?? doc?.name;
  }

  shouldShowDocumentSection(section: any): boolean {
    return (
      this.canAddDocuments ||
      (this.documentsByVisibility[section?.visibility]?.length ?? 0) > 0
    );
  }

  private resolveShowOnWebsite(visibility: string) {
    return visibility === "COMMERCIAL";
  }

  moveDocumentToVisibility(doc: any, visibility: string) {
    doc.documentVisibility = visibility;
    doc.showOnWebsite = this.resolveShowOnWebsite(visibility);

    if (visibility === "COMMERCIAL" && (!doc.description || doc.description === "---")) {
      doc.description = this.translate.instant(
        "t-m-properties.t-commercial-document-other",
      );
    }

    this.refreshDocumentsByVisibility();
  }

  private hasDocumentWithSameNameInVisibility(fileName: string, visibility: string) {
    return (this.propertyDocumentsData?.data ?? []).some(
      (doc: any) =>
        doc?.name === fileName &&
        (doc?.documentVisibility ?? "PRIVATE") === visibility,
    );
  }

  private createDocumentUploadKey(fileName: string, visibility: string) {
    return `${visibility}_${fileName}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
  }

  private isSameDocument(doc: any, target: any) {
    if (doc?.uploadKey || target?.uploadKey) {
      return doc?.uploadKey === target?.uploadKey;
    }

    return doc?.name === target?.name && doc?.order === target?.order;
  }

  canEditDocument(data: any): boolean {
    return (
      data?.isNew ||
      this.permissionsService.checkMasterPermission(
        "EDIT_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
        data?.isNew,
      ) ||
      this.permissionsService.checkPropertyAgentPermission(
        this.property,
        "EDIT_PROPERTY_DOCUMENTS",
      )
    );
  }

  canUseDocumentActions(data: any): boolean {
    return (
      data?.isNew ||
      !this.property ||
      this.canConsultDocument(data) ||
      this.canDownloadDocument(data) ||
      this.canEditDocument(data) ||
      this.canDeleteDocument(data)
    );
  }

  canConsultDocument(data: any): boolean {
    return (
      !data?.isNew &&
      this.permissionsService.hasPermission("CONSULT_PROPERTY_DOCUMENTS")
    );
  }

  canDownloadDocument(data: any): boolean {
    return (
      !data?.isNew &&
      this.permissionsService.hasPermission("DOWNLOAD_PROPERTY_DOCUMENTS")
    );
  }

  canDeleteDocument(data: any): boolean {
    return (
      data?.isNew ||
      this.permissionsService.checkMasterPermission(
        "DELETE_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
        data?.isNew,
      ) ||
      this.permissionsService.checkPropertyAgentPermission(
        this.property,
        "DELETE_PROPERTY_DOCUMENTS",
      )
    );
  }

  private refreshDocumentPermissions() {
    if (!this.property) {
      this.canAddDocuments = this.permissionsService.hasPermission(
        "ADD_PROPERTY_DOCUMENTS",
      );
      return;
    }

    this.canAddDocuments =
      this.permissionsService.checkMasterPermission(
        "ADD_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
      ) ||
      this.permissionsService.checkPropertyAgentPermission(
        this.property,
        "ADD_PROPERTY_DOCUMENTS",
      );
  }

  private loadActivatedAiServices() {
    if (!this.canViewAiIntegrations) {
      this.activatedAiServices = new Set<string>();
      this.aiServicesLoaded = true;
      return;
    }

    this.aiServicesLoaded = false;
    this.aiService.getServicesWithStatus().subscribe({
      next: (services) => {
        const activeCodes = (services ?? [])
          .filter((service: any) => service?.activated)
          .map((service: any) => String(service?.code ?? "").toUpperCase());
        this.activatedAiServices = new Set(activeCodes);
        this.aiServicesLoaded = true;
      },
      error: () => {
        this.activatedAiServices = new Set<string>();
        this.aiServicesLoaded = true;
      },
    });
  }

  /**
   * Documentos já guardados que a extração consegue ler (PDF e imagens). Os
   * que ainda não foram gravados carregam-se diretamente no painel.
   */
  requestAiExtraction() {
    const ids = (this.propertyDocumentsData?.data ?? [])
      .filter((doc: any) => {
        const id = Number(doc?.id);
        if (!Number.isInteger(id) || id <= 0 || doc?.isNew) return false;
        const name = `${doc?.name ?? doc?.originalName ?? doc?.fileName ?? ""}`;
        const type = `${doc?.type ?? doc?.mimeType ?? ""}`.toLowerCase();
        return (
          /\.(pdf|jpe?g|png|webp)$/i.test(name) ||
          type === "application/pdf" ||
          type.startsWith("image/")
        );
      })
      .map((doc: any) => Number(doc.id));
    this.aiExtractRequested.emit(ids);
  }

  canUseDocAiService(): boolean {
    if (!this.aiServicesLoaded) return false;
    return this.DOC_EXTRACTION_CODES.some((code) =>
      this.activatedAiServices.has(code),
    );
  }

  private normalizeDocExtractionOutput(output: any): Record<string, any> {
    if (output?.extraction && typeof output.extraction === "object") {
      return {
        ...output.extraction,
        locationMatch: output.locationMatch,
        structured: output.structured,
        form: output.form,
        quality: output.structured?.quality,
        evidence: output.structured?.evidence,
        documentsProcessed:
          output.documentsProcessed ?? output.extraction?.documentsProcessed,
        confidence: output.confidence ?? output.extraction?.confidence,
      };
    }
    return output ?? {};
  }

  someNewDocument(list: any[]) {
    return list && list.some((l) => l?.isNew);
  }

  async onFileChange(event: any) {
    const validFiles = ["jpg", "jpeg", "png", "pdf", "kmz", "doc", "docx"];
    this.countDoc = this.propertyDocumentsData?.data?.length;

    if (event.target?.files && event.target?.files?.length > 0) {
      this.countDoc += event.target.files.length;

      if (this.countDoc <= 50) {
        for (var i = 0; i < event.target.files.length; i++) {
          const originalFile: File = event.target.files[i];
          const file = this.normalizeUploadFile(originalFile);
          const fileType = this.resolveFileType(file);

          if (validFiles.includes(fileType)) {
            // Limite por documento; o conjunto de documentos não tem teto.
            if (file.size <= DOCUMENT_UPLOAD_MAX_BYTES) {
              const fileData = this.hasDocumentWithSameNameInVisibility(
                file.name,
                this.selectedDocumentVisibility,
              );

              if (!fileData) {
                let description = "---";
                if (this.selectedDocumentVisibility === "COMMERCIAL") {
                  const commercialDescription =
                    await this.askCommercialDocumentDescription();
                  if (!commercialDescription) continue;
                  description = commercialDescription;
                }
                const uploadKey = this.createDocumentUploadKey(
                  file.name,
                  this.selectedDocumentVisibility,
                );
                (file as any).uploadKey = uploadKey;

                this.propertyDocumentsData.data.unshift({
                  name: file.name,
                  size: file.size,
                  type: file.type || `application/${fileType}`,
                  createdAt: new Date(),
                  isNew: true,
                  description,
                  documentVisibility: this.selectedDocumentVisibility,
                  showOnWebsite: this.resolveShowOnWebsite(this.selectedDocumentVisibility),
                  propertyDocumentTypeId: this.pendingDocumentTypeId ?? null,
                  uploadKey,
                  order: this.propertyDocumentsData.data.length + 1,
                });

                this.propertyDocumentsData.dataUpload.unshift(file);
              } else {
                const msg = this.translate.instant(
                  "t-m-properties.t-the-files-must-not-have-the-same-name",
                );
                this.errorMsg(msg);
              }
            } else {
              const msg = this.translate.instant(
                "t-m-properties.t-the-maximum-file-upload-limit-should-not-exceed-50MB",
              );
              this.errorMsg(msg);
            }
          } else {
            const msg = this.translate.instant(
              "t-m-properties.t-document-formats-must-be-images-and-pdf",
            );
            this.errorMsg(msg);
          }
        }
        event.target.value = "";
        this.pendingDocumentTypeId = null;

        this.refreshDocumentsByVisibility();
        this.refreshDocumentsWarnings();
      } else {
        const msg = this.translate.instant(
          "t-m-properties.t-the-number-of-documents-must-be-50",
        );
        this.errorMsg(msg);
      }
    }
  }

  // -------------------------------------------- linhas compactas e checklist

  isPdf(doc: any): boolean {
    return this.formatType(doc?.type) === "pdf";
  }

  /** Word (.doc/.docx), pelo mimetype ou, à falta dele, pela extensão do nome. */
  isWord(doc: any): boolean {
    if (this.wordTypeOf(doc?.type)) return true;
    const extension = `${doc?.name ?? ""}`.split(".").pop()?.toLowerCase();
    return extension === "doc" || extension === "docx";
  }

  documentIconName(doc: any): string {
    if (this.isPdf(doc)) return "ri-file-pdf-fill";
    if (this.isWord(doc)) return "ri-file-word-fill";
    return "ri-image-2-line";
  }

  documentIconColor(doc: any): string {
    if (this.isPdf(doc)) return "text-danger";
    if (this.isWord(doc)) return "text-info";
    return "text-primary";
  }

  private wordTypeOf(type: any): "doc" | "docx" | null {
    const mime = `${type ?? ""}`.toLowerCase();
    if (mime.includes("wordprocessingml")) return "docx";
    if (mime.endsWith("/msword")) return "doc";
    return null;
  }

  /** A descrição, excepto o marcador "---" dos documentos sem descrição. */
  descriptionOf(doc: any): string {
    const text = `${doc?.description ?? ""}`.trim();
    return text && text !== "---" ? text : "";
  }

  visibilityLabelKey(visibility: string | null | undefined): string {
    return (
      this.documentSections.find((section) => section.visibility === visibility)
        ?.label ?? "t-m-properties.t-private-documents"
    );
  }

  /** O chip do tipo: escolher um tipo, ou limpar. */
  setDocumentType(doc: any, typeId: number | null): void {
    if (!this.canEditDocument(doc)) return;
    doc.propertyDocumentTypeId = typeId;
    this.onDocumentTypeChanged(doc);
  }

  toggleShowOnWebsite(doc: any): void {
    if (!this.canEditDocument(doc)) return;
    doc.showOnWebsite = !doc.showOnWebsite;
  }

  // ----------------------------------------------- dropzone por secção

  onSectionDragOver(event: DragEvent, visibility: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.canAddDocuments) this.draggingVisibility = visibility;
  }

  onSectionDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.draggingVisibility = null;
  }

  /**
   * Largar ficheiros numa secção: entram nessa visibilidade, sem tipo
   * pré-escolhido. `onFileChange` só lê `target.files` e limpa `target.value`,
   * por isso um alvo mínimo chega — não vale a pena duplicar as validações.
   */
  onSectionDrop(event: DragEvent, visibility: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingVisibility = null;

    const files = event.dataTransfer?.files;
    if (!files?.length || !this.canAddDocuments) return;

    this.selectedDocumentVisibility = visibility;
    this.pendingDocumentTypeId = null;
    void this.onFileChange({ target: { files, value: "" } });
  }

  /** Clique na dropzone da secção: o input escondido dessa secção. */
  onSectionPick(event: any, visibility: string): void {
    this.selectedDocumentVisibility = visibility;
    this.pendingDocumentTypeId = null;
    void this.onFileChange(event);
  }

  addDoc(visibility = "PRIVATE", documentTypeId: number | null = null) {
    this.selectedDocumentVisibility = visibility;
    // Tipo pré-escolhido só quando se vem de uma linha da checklist; o botão
    // genérico limpa-o (senão um diálogo cancelado deixava o tipo pendurado).
    this.pendingDocumentTypeId = documentTypeId;
    const inputFile = document.getElementById("input-file-doc");
    inputFile?.click();
  }

  private async askCommercialDocumentDescription(): Promise<string | null> {
    const floorPlan = this.translate.instant(
      "t-m-properties.t-commercial-document-floor-plan",
    );
    const finishingMap = this.translate.instant(
      "t-m-properties.t-commercial-document-finishing-map",
    );
    const other = this.translate.instant(
      "t-m-properties.t-commercial-document-other",
    );

    const result = await Swal.fire({
      title: this.translate.instant("t-m-properties.t-commercial-document-type"),
      html: `
        <div class="text-start">
          <label class="form-label">${this.translate.instant("t-m-properties.t-type")}</label>
          <select id="commercial-document-type" class="form-select mb-3">
            <option value="" selected>${this.translate.instant("t-m-properties.t-commercial-document-type-placeholder")}</option>
            <option value="${floorPlan}">${floorPlan}</option>
            <option value="${finishingMap}">${finishingMap}</option>
            <option value="OTHER">${other}</option>
          </select>
          <div id="commercial-document-other-wrap" style="display:none;">
            <label class="form-label">${this.translate.instant("t-m-properties.t-description")}</label>
            <input id="commercial-document-description" class="form-control"
              maxlength="200"
              placeholder="${this.translate.instant("t-m-properties.t-commercial-document-description-placeholder")}" />
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: this.translate.instant("t-save"),
      cancelButtonText: this.translate.instant("t-close"),
      customClass: { confirmButton: "btn btn-primary-custom" },
      didOpen: () => {
        const select = document.getElementById(
          "commercial-document-type",
        ) as HTMLSelectElement | null;
        const input = document.getElementById(
          "commercial-document-description",
        ) as HTMLInputElement | null;
        const otherWrap = document.getElementById(
          "commercial-document-other-wrap",
        ) as HTMLDivElement | null;

        select?.addEventListener("change", () => {
          if (!input || !select || !otherWrap) return;
          const isOther = select.value === "OTHER";
          otherWrap.style.display = isOther ? "block" : "none";
          input.value = "";
          if (isOther) input.focus();
        });
      },
      preConfirm: () => {
        const select = document.getElementById(
          "commercial-document-type",
        ) as HTMLSelectElement | null;
        const input = document.getElementById(
          "commercial-document-description",
        ) as HTMLInputElement | null;
        const selectedValue = `${select?.value ?? ""}`.trim();
        const value =
          selectedValue === "OTHER"
            ? `${input?.value ?? ""}`.trim()
            : selectedValue;

        if (!value) {
          Swal.showValidationMessage(
            this.translate.instant(
              "t-m-properties.t-commercial-document-description-required",
            ),
          );
          return false;
        }

        return value;
      },
    });

    return result.isConfirmed ? `${result.value}`.trim() : null;
  }

  deleteDoc(doc: any) {
    Swal.fire({
      title: this.translate.instant(
        "t-m-properties.t-are-you-sure-that-you-want-to-delete-a-document",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f46a6a",
      confirmButtonText: this.translate.instant("t-yes"),
      cancelButtonText: this.translate.instant("t-close"),
    }).then((result) => {
      if (result.value) {
        if (doc.isNew) {
          this.propertyDocumentsData.data =
            this.propertyDocumentsData.data.filter(
              (d: any) => !this.isSameDocument(d, doc),
            );
          this.propertyDocumentsData.dataUpload =
            this.propertyDocumentsData.dataUpload.filter(
              (d: any) =>
                doc.uploadKey
                  ? (d as any).uploadKey !== doc.uploadKey
                  : d.name !== doc.name,
            );
        } else {
          this.propertyDocumentsData.data =
            this.propertyDocumentsData.data.filter(
              (d: any) => d.id !== doc.id && d.order !== doc.order,
            );
          this.propertyDocumentsData.dataDeletefiles.push(doc);
        }
        let orderMax = this.propertyDocumentsData.data.length;
        this.propertyDocumentsData.data.forEach((el: any) => {
          el.order = orderMax;
          orderMax--;
        });
        this.refreshDocumentsByVisibility();
      }

      this.refreshDocumentsWarnings();
    });
  }

  editDocument(doc: any) {
    const msg = this.translate.instant(
      "t-m-properties.t-description-must-not-exceed-200-characters",
    );
    Swal.fire({
      title: `${this.translate.instant(
        "t-m-properties.t-edit-document-description",
      )}: ${doc.name}`,
      input: "textarea",
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: this.translate.instant("t-save"),
      cancelButtonText: this.translate.instant("t-close"),
      showLoaderOnConfirm: true,
      inputValue: doc.description,
      customClass: { confirmButton: "btn btn-primary-custom" },
      preConfirm(inputValue) {
        if (inputValue.length > 200) {
          return Swal.showValidationMessage(msg);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        const inputValue = result.value as unknown as string;
        if (inputValue) doc.description = inputValue;
        else doc.description = "---";
      }
    });
  }

  createDateFormat(date: any) {
    const d = new Date(date);
    return d.toLocaleString();
  }

  formatType(type: any) {
    if (!type || typeof type !== "string") return "";
    const split = type.split("/");
    return (split[1] || "").toLowerCase();
  }

  resolveFileType(file: any) {
    const extension = file?.name?.split(".")?.pop()?.toLowerCase();
    if (extension === "kmz") return "kmz";
    if (extension === "doc" || extension === "docx") return extension;
    const wordType = this.wordTypeOf(file?.type);
    if (wordType) return wordType;

    const mimeType = this.formatType(file?.type);
    if (mimeType) return mimeType;
    return extension || "";
  }

  normalizeUploadFile(file: File) {
    const extension = file?.name?.split(".")?.pop()?.toLowerCase();
    if (extension === "kmz" && file.type !== "application/vnd.google-earth.kmz") {
      return new File([file], file.name, {
        type: "application/vnd.google-earth.kmz",
        lastModified: file.lastModified,
      });
    }
    return file;
  }

  getDocumentUrl(fileName: string) {
    return `${this.environment.apiUrl}${this.environment.dirPropertiesDocs}/${encodeURIComponent(
      fileName
    )}`;
  }

  getDocumentPreviewUrl(fileName: string) {
    return `${this.getDocumentUrl(fileName)}/preview`;
  }

  previewDocument(doc: any) {
    if (!doc?.fileName) return;

    // O browser não mostra Word em iframe: abre/descarrega noutro separador.
    if (this.isWord(doc)) {
      this.openDocumentQuickly(doc);
      return;
    }

    const url = this.getDocumentPreviewUrl(doc.fileName);
    const fileType = this.formatType(doc.type);
    const isPdf = fileType === "pdf";
    const isImage = ["png", "jpg", "jpeg", "webp"].includes(fileType);

    const previewHtml = isPdf
      ? `<iframe src="${url}" style="width:100%;height:75vh;border:0;" title="document-preview"></iframe>`
      : isImage
        ? `<div class="text-center"><img src="${url}" alt="${doc.name}" style="max-width:100%;max-height:75vh;object-fit:contain;" /></div>`
        : `<iframe src="${url}" style="width:100%;height:75vh;border:0;" title="document-preview"></iframe>`;

    Swal.fire({
      title: `${this.translate.instant("t-preview")}: ${doc.name}`,
      html: previewHtml,
      width: "80rem",
      showCloseButton: true,
      showConfirmButton: false,
    });
  }

  openDocumentQuickly(doc: any) {
    if (!doc?.fileName) return;

    const url = this.getDocumentPreviewUrl(doc.fileName);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  formatSizeUnits(bytes: any) {
    if (bytes >= 1073741824) {
      bytes = (bytes / 1073741824).toFixed(2) + " GB";
    } else if (bytes >= 1048576) {
      bytes = (bytes / 1048576).toFixed(2) + " MB";
    } else if (bytes >= 1024) {
      bytes = (bytes / 1024).toFixed(2) + " KB";
    } else if (bytes > 1) {
      bytes = bytes + " bytes";
    } else if (bytes == 1) {
      bytes = bytes + " byte";
    } else {
      bytes = "0 bytes";
    }
    return bytes;
  }

  checkSize(bytes: any) {
    bytes = (bytes / 1048576).toFixed(2);
    return bytes;
  }

  getTotalSize() {
    let filesSize = 0;
    this.propertyDocumentsData.data.forEach((el: any) => {
      filesSize += el.size;
    });
    return this.formatSizeUnits(filesSize);
  }

  /**
   * Title sweet alert
   * @param Error Msg modal content
   */
  errorMsg(errorMsg: string) {
    Swal.fire({
      title: "Oops...",
      text: errorMsg,
      icon: "warning",
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translate.instant("t-close"),
    });
  }

  projectListWidgets = [
    {
      icon: "ri-folder-zip-line",
      icon_color: "secondary",
      name: "Artboard-documents.zip",
      type: "Zip",
      size: "4.57",
      date: "12 Dec 2021",
    },
    {
      icon: "ri-file-pdf-fill",
      icon_color: "danger",
      name: "Bank Management System",
      type: "PDF",
      size: "8.89",
      date: "24 Nov 2021",
    },
    {
      icon: "ri-video-line",
      icon_color: "secondary",
      name: "Tour-video.mp4",
      type: "MP4",
      size: "14.62",
      date: "19 Nov 2021",
    },
    {
      icon: "ri-file-excel-fill",
      icon_color: "success",
      name: "Account-statement.xsl",
      type: "XSL",
      size: "2.38",
      date: "14 Nov 2021",
    },
    {
      icon: "ri-folder-fill",
      icon_color: "warning",
      name: "Project Screenshots Collection",
      type: "Floder",
      size: "87.24",
      date: "08 Nov 2021",
    },
    {
      icon: "ri-image-2-fill",
      icon_color: "danger",
      name: "Velzon-logo.png",
      type: "PNG",
      size: "879",
      date: "02 Nov 2021",
    },
  ];

  private loadDownloads(propertyId: string) {
    this.propertiesService.getDocumentDownloads(propertyId).subscribe({
      next: (rows) => (this.documentDownloads = rows ?? []),
      error: () => (this.documentDownloads = []),
    });
  }

  private refreshDocumentsWarnings(): void {
    this.propertyDocumentsData.errors = this.buildDocumentsErrors();

    this.documentsMinError = this.propertyDocumentsData.errors.find(
      (error: any) => error.key === "documents.mediaMin" && error.type?.warning,
    );

    // Com checklist tipada, a faixa "mínimo de N ficheiros" só confundia: a
    // lista de obrigatórios no topo já diz o que falta. O erro continua em
    // `errors` (pesa nos avisos do imóvel); só a faixa desaparece.
    this.showDocumentsMinWarning =
      !!this.documentsMinError &&
      !(this.typedMode && this.checklistRows.length > 0);

    this.eventService.broadcast("check-warning-add-property");
  }

  private buildDocumentsErrors(): any[] {
    const errors: any[] = [];

    // Documentos obrigatórios por tipo de imóvel (só no modo tipado).
    if (this.typedMode && this.requiredMissingNames.length > 0) {
      errors.push({
        key: "documents.types",
        type: { warning: true },
        missing: [...this.requiredMissingNames],
      });
    }

    const enabled =
      this.propertyValidationService.isEnabled("documents.mediaMin");
    if (!enabled) return errors;

    const minDoc =
      this.propertyValidationService.minValue("documents.mediaMin") ?? 0;
    if (minDoc <= 0) return errors;

    const activeCount = this.propertyDocumentsData?.data?.length ?? 0;

    if (activeCount < minDoc) {
      errors.push({
        key: "documents.mediaMin",
        type: { warning: true },
        valueMin: minDoc,
        activeCount,
      });
    }
    return errors;
  }

  ngOnDestroy(): void {
    this.evSub?.unsubscribe();
    this.subscriptionRules?.unsubscribe();
    this.warningEvSub?.unsubscribe();
    this.applicableSub?.unsubscribe();
  }
}
